import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type { BulkTransitionResult, WorkItemSummary } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Textarea } from "../../shared/ui";

/**
 * What a reviewer wants changed, said once or task by task.
 *
 * <p>A reviewer sending six tasks back usually has one thing to say — they all missed the same
 * thing — and occasionally has something specific about one of them. Offering only the shared
 * note flattens six problems into one sentence; offering only per-task notes makes them type the
 * same sentence six times. Both at once, and the individual note wins where there is one.
 */
export function RequestChangesDrawer({
  items,
  open,
  onClose,
  onDone,
}: {
  items: WorkItemSummary[];
  open: boolean;
  onClose: () => void;
  /** True when every item moved. False leaves the selection alone so the refusal stays on screen. */
  onDone: (allMoved: boolean) => void;
}) {
  const [shared, setShared] = useState("");
  const [perItem, setPerItem] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<BulkTransitionResult[]>([]);

  useEffect(() => {
    if (open) {
      setShared("");
      setPerItem({});
      setError(null);
      setRefused([]);
    }
  }, [open]);

  /* Every task has to carry a reason. A task sent back with nothing said about it gives the
     developer a status change and no way to find out what is wrong with it. */
  const unexplained = items.filter(
    (item) => shared.trim() === "" && (perItem[item.id] ?? "").trim() === "",
  );

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const answered = await api.post<BulkTransitionResult[]>(
        "/api/v1/work-items/reviews/request-changes",
        {
          workItemIds: items.map((item) => item.id),
          reviewerId: null,
          comment: shared.trim() || null,
          comments: Object.fromEntries(
            Object.entries(perItem)
              .filter(([, note]) => note.trim() !== "")
              .map(([id, note]) => [id, note.trim()]),
          ),
        },
      );
      const stuck = answered.filter((row) => !row.moved);
      onDone(stuck.length === 0);
      if (stuck.length === 0) {
        onClose();
      } else {
        setRefused(stuck);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not send those back.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Request changes"
      subtitle={`${items.length} task${items.length === 1 ? "" : "s"} going back to development`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={unexplained.length > 0 || saving}
          >
            Send back
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="info">
          Each of these goes back to In progress, and its development-completed date is withdrawn.
          Whatever you write here is what the developer will read.
        </InlineAlert>

        <Field
          label="Say it to all of them"
          hint="Used for every task below that has nothing of its own."
        >
          <Textarea
            rows={3}
            value={shared}
            onChange={(event) => setShared(event.target.value)}
            placeholder="The rounding helper is not used anywhere — please switch these over to it."
          />
        </Field>

        <div className="space-y-3">
          {items.map((item) => (
            <Field
              key={item.id}
              label={`${item.itemKey} — ${item.title}`}
              hint={
                (perItem[item.id] ?? "").trim() !== ""
                  ? "This one gets its own note."
                  : shared.trim() !== ""
                    ? "Will get the shared note."
                    : "Needs a note, here or above."
              }
            >
              <Textarea
                rows={2}
                value={perItem[item.id] ?? ""}
                onChange={(event) =>
                  setPerItem((current) => ({ ...current, [item.id]: event.target.value }))
                }
                placeholder="Only if this one needs something said about it."
              />
            </Field>
          ))}
        </div>

        {unexplained.length > 0 && (
          <InlineAlert tone="warning">
            {unexplained.length} of these has nothing said about it. Write something above, or a
            note against each one.
          </InlineAlert>
        )}

        {refused.length > 0 && (
          <InlineAlert tone="warning">
            <div className="space-y-1">
              <p>{refused.length} did not go back. The rest did.</p>
              {refused.map((row) => (
                <p key={row.workItemId} className="text-sm">
                  <span className="font-medium">{row.itemKey ?? "Item"}</span> —{" "}
                  {row.refusedBecause}
                </p>
              ))}
            </div>
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}
